import { useCallback, useState } from 'react';
import { useAuth } from '@/features/auth';
import {
  useCanDeclareMasterAccount,
  useDeclareMasterAccount
} from '@/features/admin/components/system-settings';

/** `skip` means the step does not apply; `deciding` waits for the people. */
export type AccountTypeStepPhase = 'deciding' | 'ready' | 'skip';

/** Room for the caller and machine identities; a longer list means others and skips. */
const FOUNDER_CHECK_PAGE_SIZE = 5;

/**
 * State for the onboarding account-type step. It is offered only to the founder: a caller who may
 * declare a master account and is the organization's only person, so no one needs classifying.
 */
export function useAccountTypeStep(open: boolean) {
  const { user } = useAuth();
  const canDeclare = useCanDeclareMasterAccount(!!user?.account?.managingMasterAccount);
  // Read from the start of onboarding, so the step is usually decided before it is reached.
  const { people, morePeople, peopleError, ready, declare, declaring } = useDeclareMasterAccount(
    open && canDeclare,
    { pageSize: FOUNDER_CHECK_PAGE_SIZE, readAllPages: false }
  );
  // Once a declaration is confirmed the step stays put, whatever the people reload to.
  const [attempted, setAttempted] = useState(false);
  const [refused, setRefused] = useState(false);

  // People that could not be read count as others: declaring then belongs in the settings.
  const phase: AccountTypeStepPhase = attempted
    ? 'ready'
    : !canDeclare || peopleError
      ? 'skip'
      : !ready
        ? 'deciding'
        : people.length === 0 && !morePeople
          ? 'ready'
          : 'skip';

  const confirmDeclare = useCallback(async () => {
    setAttempted(true);
    if ((await declare()) === 'peopleChanged') setRefused(true);
  }, [declare]);

  return { phase, declare: confirmDeclare, declaring, refused };
}
